/**
 * Fechar o mês — e corrigir o mês já fechado.
 *
 * O wizard original coleta três meses do zero toda vez. Enquanto for só isso, o
 * usuário não tem motivo para voltar no mês seguinte, e uma assinatura mensal
 * sem motivo mensal de retorno churna no segundo ciclo. Aqui ele responde os
 * mesmos grupos para UMA competência, e ela é anexada ao histórico.
 *
 * DOIS CAMINHOS, DECIDIDOS PELO MÊS ESCOLHIDO — e essa é a correção mais
 * importante desta tela:
 *
 * - **Mês novo** consome cota do plano, passa por preview e é gravado inteiro
 *   (`POST /api/periodos`).
 * - **Mês já lançado** abre PRÉ-PREENCHIDO e manda só o que mudou
 *   (`PATCH /api/periodos/:competencia`). Não consome cota em plano nenhum,
 *   porque cobrar por corrigir o próprio dado puniria justamente quem está
 *   caprichando nele.
 *
 * O defeito que morreu aqui: escolher um mês já lançado abria o formulário EM
 * BRANCO e oferecia "Substituir e fechar". Julho tinha 7 contas e R$ 95.000 de
 * faturamento; preencher só "Salários" e substituir deixava o mês com 1 conta,
 * faturamento R$ 0,00 e prejuízo de R$ 21.000 — a tela apagava por omissão
 * exatamente embaixo da frase "corrigir os meses que você já lançou continua
 * liberado". Substituir mês inteiro por formulário manual não existe mais nesta
 * tela; trocar um mês inteiro é reimportar o documento inteiro.
 *
 * Três etapas: escolher o mês, preencher, conferir o que vai ser gravado.
 * A conferência não é cerimônia — é onde o limite do plano avisa antes de
 * frustrar, e onde a correção mostra conta a conta o que vai mudar.
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Button, Card, CardContent, Chip, Divider, Alert, AlertTitle,
  Stack, TextField, Typography, MenuItem, CircularProgress
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

import { GRUPOS } from '../../config/planoContas';
import {
  loteDoMes, rotuloDaCompetencia, rotuloLongoDaCompetencia, gruposDoLote,
  alteracoesDoMes, contasCorrigidas, totaisDasContas, plural
} from '../../services/loteDoMes';
import { listarPeriodos, preverLotes, fecharMes, lerMes, corrigirMes } from '../../services/periodoService';

const NOMES_MES = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
];

const rotuloDoMes = (competencia) => {
  const [ano, mes] = String(competencia).split('-');
  return `${NOMES_MES[Number(mes) - 1]} de ${ano}`;
};

/**
 * Os 18 meses até o mês passado, MAIS todo mês que a empresa já lançou.
 *
 * Os 18 são a oferta de fechamento: ninguém fecha um mês que não terminou. Mas
 * a lista fechada deixava mês gravado SEM caminho de correção — o questionário
 * aceita qualquer mês dos três últimos anos, então quem lançasse dezembro em
 * agosto ficava com dezembro no banco e sem nenhuma tela para arrumá-lo. A
 * união resolve pelo lado certo: todo mês que EXISTE é corrigível, e nenhum
 * mês futuro que não existe é oferecido para fechamento.
 */
const mesesOferecidos = (jaLancados = []) => {
  const hoje = new Date();
  const passados = Array.from({ length: 18 }, (_, i) => {
    const d = new Date(hoje.getFullYear(), hoje.getMonth() - 1 - i, 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  });

  return [...new Set([...passados, ...(jaLancados || [])])]
    .sort((a, b) => b.localeCompare(a))
    .map((competencia) => ({ competencia, rotulo: rotuloDoMes(competencia) }));
};

const contasIniciais = () =>
  Object.fromEntries(GRUPOS.map((g) => [g.id, g.contas.map((c) => ({ ...c, valor: '' }))]));

export default function FecharMes({ aoConcluir, aoVerPlanos }) {
  const [etapa, setEtapa] = useState(0);
  const [competencia, setCompetencia] = useState('');
  const [grupos, setGrupos] = useState(contasIniciais);
  // O formulário como ele foi CARREGADO. É contra isto que o delta da correção
  // é calculado — sem a foto inicial não dá para saber o que o usuário tocou.
  const [iniciais, setIniciais] = useState(contasIniciais);
  const [vigente, setVigente] = useState(null);
  const [situacao, setSituacao] = useState(null);
  const [previa, setPrevia] = useState(null);
  const [carregando, setCarregando] = useState(false);
  const [carregandoMes, setCarregandoMes] = useState(false);
  const [erro, setErro] = useState(null);
  const [bloqueio, setBloqueio] = useState(null);

  useEffect(() => {
    listarPeriodos().then(setSituacao).catch((e) => setErro(e.message));
  }, []);

  const jaLancados = useMemo(
    () => situacao?.cobertura?.competencias || [],
    [situacao]
  );

  const meses = useMemo(() => mesesOferecidos(jaLancados), [jaLancados]);
  const semVagas = situacao?.plano?.restantes === 0;
  const eMesNovo = Boolean(competencia) && !jaLancados.includes(competencia);
  const corrigindo = Boolean(competencia) && !eMesNovo;

  const lote = useMemo(
    () => (competencia && eMesNovo ? loteDoMes({ competencia, grupos }) : null),
    [competencia, grupos, eMesNovo]
  );

  const alteracoes = useMemo(
    () => (corrigindo ? alteracoesDoMes({ grupos, iniciais }) : { contas: [] }),
    [corrigindo, grupos, iniciais]
  );

  const alterarValor = (grupoId, nome, valor) =>
    setGrupos((atual) => ({
      ...atual,
      [grupoId]: atual[grupoId].map((c) => (c.nome === nome ? { ...c, valor } : c))
    }));

  /**
   * Trocar de mês zera o formulário; voltar ao MESMO mês não zera nada.
   *
   * O estado nascia uma vez só e nunca era reposto: preencher "Vendas à vista =
   * 40.000" para maio, voltar e escolher julho levava o número de maio para
   * dentro de julho — a conferência de julho mostrava faturamento de maio, e um
   * clique gravaria. A comparação com o valor anterior é o que impede o outro
   * extremo: repor a cada render apagaria 30 campos digitados quando o usuário
   * volta da conferência para corrigir.
   */
  const escolherMes = async (valor) => {
    if (valor === competencia) return;
    setCompetencia(valor);
    setPrevia(null);
    setErro(null);
    setVigente(null);

    const zerado = contasIniciais();
    setGrupos(zerado);
    setIniciais(zerado);

    if (!valor || !jaLancados.includes(valor)) return;

    setCarregandoMes(true);
    try {
      const mes = await lerMes(valor);
      const { grupos: preenchidos, foraDoFormulario } = gruposDoLote(mes.contas);
      setGrupos(preenchidos);
      setIniciais(preenchidos);
      setVigente({ ...mes, foraDoFormulario });
    } catch (e) {
      setErro(`Não consegui ler ${rotuloDaCompetencia(valor)}: ${e.message}`);
      setCompetencia('');
    } finally {
      setCarregandoMes(false);
    }
  };

  const irParaConferencia = async () => {
    setCarregando(true);
    setErro(null);
    // O bloqueio é da TENTATIVA anterior. Sem limpar, o usuário que foi barrado
    // num mês novo e depois corrigiu um mês existente com sucesso terminaria
    // vendo "fechado" logo abaixo de "não foi gravado" — dois vereditos
    // contraditórios na mesma tela.
    setBloqueio(null);
    try {
      // Correção não passa por preview: ela não disputa cota com nada, e o que
      // o usuário precisa conferir é a lista do que mudou, não o veredito do plano.
      setPrevia(corrigindo ? null : await preverLotes([lote]));
      setEtapa(2);
    } catch (e) {
      setErro(e.message);
    } finally {
      setCarregando(false);
    }
  };

  const gravar = async () => {
    setCarregando(true);
    setErro(null);
    try {
      const resultado = corrigindo
        ? await corrigirMes(competencia, alteracoes)
        : await fecharMes([lote]);

      if (resultado.bloqueados?.length) {
        // Estado próprio, e não `setErro`: isto não é falha, é limite de plano —
        // e ele precisa carregar o caminho de saída, não só a recusa.
        setBloqueio(resultado.bloqueados.map((b) => b.competencia));
        return;
      }
      if (resultado.conflitos?.length) {
        // Só acontece numa corrida: o mês virou existente entre a escolha e o
        // clique. Nada é gravado por cima — o caminho é recarregar e corrigir.
        setErro(`${rotuloDaCompetencia(competencia)} passou a existir enquanto você preenchia. `
          + 'Nada foi gravado. Recarregue a tela e escolha o mês de novo para corrigi-lo.');
        return;
      }
      setEtapa(3);
      if (aoConcluir) aoConcluir(resultado);
    } catch (e) {
      setErro(e.message);
    } finally {
      setCarregando(false);
    }
  };

  const item = previa?.itens?.[0];
  const restantes = situacao?.plano?.restantes;

  // Conta com `valor: null` é conta INFORMADA EM BRANCO, e contá-la como
  // lançada infla o número que a tela mostra. Elas aparecem sozinhas: a
  // projeção alinha o mesmo elenco de contas em todos os meses, e o mês que
  // nunca teve aquela conta recebe a posição vazia.
  const contasInformadas = (vigente?.contas || []).filter((c) => typeof c.valor === 'number').length;

  // Os totais que a correção vai produzir. Só existem para mês de origem
  // manual: num mês de planilha o total é dado do documento e a correção não o
  // recalcula — a coluna Visível/Invisível faz a soma das contas não fechar.
  const totaisDepois = useMemo(() => {
    if (!corrigindo || vigente?.origem !== 'manual') return null;
    return totaisDasContas(contasCorrigidas(vigente?.contas, alteracoes));
  }, [corrigindo, vigente, alteracoes]);

  return (
    <Box sx={{ maxWidth: 760, mx: 'auto', p: { xs: 2, sm: 3 } }}>
      {/* h1 de verdade: era `h5` sem `component`, o que rende um <h5> solto e
          deixa a tela sem título nenhum no sumário do leitor de tela. */}
      <Typography variant="h3" component="h1" gutterBottom>Fechar o mês</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Um mês por vez, anexado ao que você já lançou. Escolher um mês que já está
        lançado abre ele preenchido, para corrigir.
      </Typography>

      {erro && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErro(null)}>{erro}</Alert>}

      {bloqueio && (
        <Alert severity="warning" sx={{ mb: 2 }} action={acaoDePlanos(aoVerPlanos, bloqueio)}>
          <AlertTitle>
            {/* Nomeia TODOS os meses recusados: mandar o usuário descobrir quais
                dos seus meses não entraram é a pior tarefa que este produto pode dar. */}
            {listaDeMeses(bloqueio)} {bloqueio.length === 1 ? 'não foi gravado' : 'não foram gravados'}
          </AlertTitle>
          O plano atual não comporta mais um mês. Nada do que você já lançou foi alterado.
        </Alert>
      )}

      {/* O aviso de limite aparece ANTES do trabalho, não depois de 30 campos —
          e some enquanto o usuário CORRIGE: falar de cota cheia em cima de uma
          operação que não consome cota é ruído que faz duvidar da promessa. */}
      {semVagas && !corrigindo && (
        <Alert severity="warning" sx={{ mb: 2 }} action={acaoDePlanos(aoVerPlanos)}>
          <AlertTitle>Você já usou os {situacao.plano.limiteCompetencias} meses do plano {situacao.plano.nome}</AlertTitle>
          Para acrescentar mais um mês e acompanhar a evolução, é preciso assinar.
          Corrigir ou completar os meses que você já lançou continua liberado.
        </Alert>
      )}

      {etapa === 0 && (
        <Card variant="outlined">
          <CardContent>
            <TextField
              select fullWidth label="Que mês você quer fechar?"
              value={competencia} onChange={(e) => escolherMes(e.target.value)}
              helperText="Só aparecem meses já encerrados. Os marcados já estão lançados — escolher um deles é corrigir."
            >
              {meses.map((m) => (
                <MenuItem key={m.competencia} value={m.competencia}>
                  {m.rotulo}
                  {jaLancados.includes(m.competencia) && (
                    <Chip size="small" label="já lançado" sx={{ ml: 1 }} />
                  )}
                </MenuItem>
              ))}
            </TextField>

            {carregandoMes && (
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 2 }}>
                <CircularProgress size={16} />
                <Typography variant="caption" color="text.secondary">
                  Buscando o que já está lançado nesse mês…
                </Typography>
              </Stack>
            )}

            {corrigindo && vigente && !carregandoMes && (
              <Alert severity="info" sx={{ mt: 2 }}>
                <AlertTitle>
                  {rotuloLongoDaCompetencia(competencia)} já está lançado — você vai corrigir
                </AlertTitle>
                O formulário abre com {plural(contasInformadas, 'conta', 'contas')} que
                esse mês já tem. Só o que você mudar é enviado, o resto fica como está.
                Corrigir não consome nada do seu plano.
              </Alert>
            )}

            {restantes !== null && restantes !== undefined && restantes > 0 && eMesNovo && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Depois deste, {restantes - 1 === 1 ? 'ainda cabe' : 'ainda cabem'}{' '}
                {plural(restantes - 1, 'mês', 'meses')} no plano {situacao.plano.nome}.
              </Typography>
            )}

            {/* A RECUSA DO 4º MÊS NOMEIA O MÊS.
                Antes disto o botão apenas ficava cinza, sem nenhum texto ligado a
                ele: a pessoa escolhia "Maio de 2026", nada acontecia, e o único
                aviso era o genérico do topo — que aparece antes de qualquer
                escolha e não cita competência nenhuma. */}
            {semVagas && eMesNovo && (
              <Alert
                severity="warning"
                sx={{ mt: 2 }}
                action={acaoDePlanos(aoVerPlanos, [competencia])}
              >
                <AlertTitle>{rotuloLongoDaCompetencia(competencia)} não cabe no plano {situacao.plano.nome}</AlertTitle>
                O plano {situacao.plano.nome} acompanha {situacao.plano.limiteCompetencias} meses, e
                você já tem {plural(situacao.cobertura?.total || 0, 'mês lançado', 'meses lançados')}.
                Para acrescentar {rotuloDaCompetencia(competencia)} é preciso assinar — corrigir os
                meses que você já lançou continua liberado.
              </Alert>
            )}

            <Button
              variant="contained" endIcon={<ArrowForwardIcon />} sx={{ mt: 3 }}
              disabled={!competencia || carregandoMes || (semVagas && eMesNovo)}
              onClick={() => setEtapa(1)}
            >
              {corrigindo ? 'Corrigir este mês' : 'Continuar'}
            </Button>
          </CardContent>
        </Card>
      )}

      {etapa === 1 && (
        <Stack spacing={2}>
          {corrigindo && (
            <Alert severity="info">
              <AlertTitle>Corrigindo {rotuloLongoDaCompetencia(competencia)}</AlertTitle>
              Os campos vieram do que está lançado. Altere o que estiver errado e acrescente o que
              faltou — o que você não tocar permanece exatamente como está.
              {vigente?.foraDoFormulario?.length > 0 && (
                <>
                  {' '}Este mês tem ainda{' '}
                  {plural(vigente.foraDoFormulario.length, 'conta que este formulário não pergunta',
                    'contas que este formulário não pergunta')}
                  ; elas continuam guardadas e não são afetadas.
                </>
              )}
            </Alert>
          )}

          {GRUPOS.map((grupo) => (
            <Card key={grupo.id} variant="outlined">
              <CardContent>
                <Typography variant="subtitle1">{grupo.titulo}</Typography>
                {grupo.ajuda && (
                  <Typography variant="caption" color="text.secondary">{grupo.ajuda}</Typography>
                )}
                <Stack spacing={1.5} sx={{ mt: 2 }}>
                  {grupos[grupo.id].map((conta) => (
                    <TextField
                      key={conta.nome} size="small" label={conta.nome}
                      value={conta.valor}
                      // O texto antigo era "deixe em branco se não teve", e ele
                      // pedia justamente a confusão que o produto proíbe: quem
                      // não teve MEDIU zero, e zero é dado. Em branco é ausência,
                      // e grupo inteiro em branco não gera total nenhum.
                      placeholder="em branco = não informado; 0 = não teve"
                      onChange={(e) => alterarValor(grupo.id, conta.nome, e.target.value)}
                      inputProps={{ inputMode: 'decimal' }}
                    />
                  ))}
                </Stack>
              </CardContent>
            </Card>
          ))}

          <Stack direction="row" spacing={1}>
            <Button startIcon={<ArrowBackIcon />} onClick={() => setEtapa(0)}>Voltar</Button>
            <Button
              variant="contained" endIcon={carregando ? <CircularProgress size={16} /> : <ArrowForwardIcon />}
              disabled={carregando || (corrigindo && alteracoes.contas.length === 0)}
              onClick={irParaConferencia}
            >
              Conferir
            </Button>
          </Stack>
          {corrigindo && alteracoes.contas.length === 0 && (
            <Typography variant="caption" color="text.secondary">
              Nada mudou ainda. Altere ou acrescente algum valor para poder conferir.
            </Typography>
          )}
        </Stack>
      )}

      {etapa === 2 && !corrigindo && (
        <Card variant="outlined">
          <CardContent>
            <Typography variant="subtitle1" gutterBottom>
              {rotuloLongoDaCompetencia(competencia)} — o que vai ser gravado
            </Typography>

            <Stack spacing={0.5} sx={{ my: 2 }}>
              <Linha rotulo="Faturamento" valor={lote?.totais['1.1']} />
              <Linha rotulo="Custos variáveis" valor={lote?.totais['3.2']} />
              <Linha rotulo="Custos fixos" valor={lote?.totais['2.1.1']} />
              <Divider sx={{ my: 1 }} />
              <Linha rotulo="Resultado do mês" valor={lote?.totais.lucroReal} destaque />
              <Typography variant="caption" color="text.secondary">
                {plural(lote?.contas.length || 0, 'conta informada', 'contas informadas')}.
              </Typography>
              {/* Travessão é ausência, e ausência precisa de explicação: sem ela
                  o usuário lê o traço como defeito da tela. */}
              {lote && lote.totais.lucroReal === undefined && (
                <Typography variant="caption" color="text.secondary">
                  O resultado do mês só aparece quando faturamento, custos variáveis e custos fixos
                  tiverem ao menos uma conta informada. Traço quer dizer não informado — se você
                  realmente não teve nada de um grupo, informe 0 numa conta dele.
                </Typography>
              )}
            </Stack>

            {item?.situacao === 'conflito' && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <AlertTitle>{rotuloDaCompetencia(competencia)} passou a existir</AlertTitle>
                Alguém (ou outra aba) lançou este mês enquanto você preenchia. Nada será gravado por
                cima: volte à escolha do mês e selecione ele de novo, que o formulário abre
                preenchido para corrigir.
              </Alert>
            )}

            {item?.situacao === 'bloqueada_por_plano' && (
              <Alert
                severity="warning"
                sx={{ mb: 2 }}
                action={acaoDePlanos(aoVerPlanos, [competencia])}
              >
                <AlertTitle>{rotuloLongoDaCompetencia(competencia)} não cabe no plano {situacao?.plano?.nome}</AlertTitle>
                {item.mensagem} {item.aindaPermitido}
              </Alert>
            )}

            <Stack direction="row" spacing={1}>
              <Button startIcon={<ArrowBackIcon />} onClick={() => setEtapa(1)}>Voltar e corrigir</Button>
              {/* Desabilitar não é avisar. O botão ficava apagado enquanto
                  gravava, sem dizer que estava trabalhando — enquanto o passo
                  anterior deste mesmo componente já fazia certo. */}
              <Button
                variant="contained"
                disabled={carregando || item?.situacao === 'bloqueada_por_plano' || item?.situacao === 'conflito'}
                startIcon={carregando ? <CircularProgress size={16} color="inherit" /> : null}
                onClick={gravar}
              >
                {carregando ? 'Fechando o mês…' : 'Fechar o mês'}
              </Button>
            </Stack>
          </CardContent>
        </Card>
      )}

      {etapa === 2 && corrigindo && (
        <Card variant="outlined">
          <CardContent>
            <Typography variant="subtitle1" gutterBottom>
              {rotuloLongoDaCompetencia(competencia)} — o que vai mudar
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {plural(alteracoes.contas.length, 'conta alterada', 'contas alteradas')} de{' '}
              {plural(contasInformadas, 'conta lançada', 'contas lançadas')}. O resto do
              mês fica como está, e isto não consome nada do seu plano.
            </Typography>

            <Stack spacing={0.5} sx={{ my: 2 }}>
              {alteracoes.contas.map((c) => (
                <Stack key={`${c.categoria}-${c.nome}`} direction="row" justifyContent="space-between" spacing={2}>
                  <Typography variant="body2" color="text.secondary">{c.nome}</Typography>
                  <Typography variant="body2">
                    {formatar(valorDe(vigente?.contas, c))} → {formatar(c.valor)}
                  </Typography>
                </Stack>
              ))}
            </Stack>

            {totaisDepois && (
              <>
                <Divider sx={{ my: 1 }} />
                <Stack spacing={0.5}>
                  <Linha
                    rotulo="Faturamento" valor={totaisDepois['1.1']}
                    antes={vigente?.totais?.['1.1']}
                  />
                  <Linha
                    rotulo="Custos variáveis" valor={totaisDepois['3.2']}
                    antes={vigente?.totais?.['3.2']}
                  />
                  <Linha
                    rotulo="Custos fixos" valor={totaisDepois['2.1.1']}
                    antes={vigente?.totais?.['2.1.1']}
                  />
                  <Divider sx={{ my: 1 }} />
                  <Linha
                    rotulo="Resultado do mês" valor={totaisDepois.lucroReal}
                    antes={vigente?.totais?.lucroReal} destaque
                  />
                </Stack>
              </>
            )}

            {corrigindo && vigente?.origem === 'planilha' && (
              <Alert severity="info" sx={{ mt: 2 }}>
                Este mês veio de planilha, e nela os totais são do próprio documento — a planilha tem
                contas invisíveis que não aparecem na lista, então a soma das contas não fecha com o
                total. Por isso a correção troca a conta e mantém os totais do documento.
              </Alert>
            )}

            <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
              <Button startIcon={<ArrowBackIcon />} onClick={() => setEtapa(1)}>Voltar e ajustar</Button>
              <Button
                variant="contained"
                disabled={carregando}
                startIcon={carregando ? <CircularProgress size={16} color="inherit" /> : null}
                onClick={gravar}
              >
                {carregando ? 'Salvando a correção…' : 'Salvar a correção'}
              </Button>
            </Stack>
          </CardContent>
        </Card>
      )}

      {etapa === 3 && (
        <Alert severity="success" icon={<CheckCircleOutlineIcon />}>
          <AlertTitle>
            {rotuloLongoDaCompetencia(competencia)} {corrigindo ? 'corrigido' : 'fechado'}
          </AlertTitle>
          {corrigindo
            ? 'A correção entrou como uma revisão nova do mesmo mês — a versão anterior continua guardada. Os indicadores já consideram os números corrigidos.'
            : 'O mês entrou na sua série. Os indicadores já consideram ele.'}
        </Alert>
      )}
    </Box>
  );
}

/**
 * O caminho de saída do bloqueio, nos lugares onde ele aparece.
 *
 * Todos diziam "é preciso assinar" e nenhum tinha para onde ir — a tela de
 * planos não existia. Agora existe, e eles apontam para ela levando junto o
 * mês que não coube: a tela de planos abre nomeando ele, em vez de abrir num
 * anúncio genérico.
 */
const acaoDePlanos = (aoVerPlanos, meses = null) =>
  aoVerPlanos ? (
    <Button
      color="inherit"
      size="small"
      onClick={() => aoVerPlanos(meses?.length ? { bloqueados: meses } : null)}
      sx={{ minHeight: 44 }}
    >
      Ver os planos
    </Button>
  ) : null;

/** "mai/26", "mai/26 e jun/26", "mai/26, jun/26 e jul/26". */
const listaDeMeses = (competencias) => {
  const nomes = (competencias || []).map(rotuloDaCompetencia);
  if (nomes.length <= 1) return nomes[0] || '';
  return `${nomes.slice(0, -1).join(', ')} e ${nomes[nomes.length - 1]}`;
};

const valorDe = (contas, alvo) => {
  const achada = (contas || []).find((c) => c.categoria === alvo.categoria && c.nome === alvo.nome);
  return achada ? achada.valor : null;
};

const formatar = (v) =>
  typeof v === 'number'
    ? v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
    : '—';

function Linha({ rotulo, valor, antes, destaque }) {
  const mudou = antes !== undefined && antes !== valor;
  return (
    <Stack direction="row" justifyContent="space-between">
      <Typography variant={destaque ? 'subtitle2' : 'body2'} color={destaque ? 'text.primary' : 'text.secondary'}>
        {rotulo}
      </Typography>
      <Typography
        variant={destaque ? 'subtitle2' : 'body2'}
        color={destaque && valor < 0 ? 'error.main' : 'text.primary'}
      >
        {mudou ? `${formatar(antes)} → ${formatar(valor)}` : formatar(valor)}
      </Typography>
    </Stack>
  );
}
